import type { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import type { ISubscription } from '@microsoft/signalr';

import { trackEvent } from 'analytics/appInsights';
import { DataApiClient } from 'api/data/client-dataapi';
import { safeResolve } from 'api/resolve';
import type { OutputOperationResult } from 'api/types';
import type { EventHistoryInfo } from 'pages/projects/projectPage/selectedProject/projectHistory/EventHistory.types';
import { EventLogOperation } from 'pages/projects/projectPage/selectedProject/projectHistory/EventHistoryCard';
import type { RequestResultSetUpdateInstruction } from 'state/data/dataActions';
import { type GridSpecification, type PropertyValue, getNodeProperties } from 'state/data/dataState';
import { DELETE_NODE_STATE } from 'state/deleteNodeState/deleteNodeState';
import {
  requestFiltersParams,
  requestPropertyFiltersParams,
  requestSortParams,
  updateRootRows
} from 'state/grids/utils/rowActionsUtils';
import { HISTORY_STORE } from 'state/history/historyStore';
import { PENDING_EDITS_STATE } from 'state/pendingEdits/pendingEditsState';
import type { ColumnSpecification } from 'state/selectedProject/selectedProjectHooks';
import type { SignalRActions } from 'state/signalr/SignalRActions';
import type {
  DataHubStreamInvocation,
  GridDataRequest,
  IQueryRequestContext,
  QueryOptions
} from 'state/signalr/signalrState';
import type { SemNode } from 'types/data/semNode';
import { devLog } from 'utils/dev-utils';
import { hasValue } from 'utils/values';
import { calculateNodesWithProperties } from 'worker/calculateNodesWithProperties';

import { createResultSetEntryId } from './createResultSetEntryId';
import { getResultSetEntry } from './getResultSetEntry';
import type { ColumnFilter, PropertyFilter } from './gridState';
import { GRID_STORE, GetPossibleNewRoots, getResultSetsToUpdate } from './gridState';
import type { GridLoadingContext } from './utils/gridStatistics';
import { getColumnSpecificationStatistics } from './utils/gridStatistics';

export interface NodeIdFilter {
  NodeTraversalId: string;
  Ids: string[];
  AllowBlanks: boolean;
}

export interface PropertyFilterDto {
  PropertyTraversalId: string;
  Values: PropertyValue[];
  AllowBlanks: boolean;
}

export interface Sort {
  Order: number;
  PropertyTraversalId: string;
  IsDescending: boolean;
}

export class GridActions {
  private readonly dataApiClient: DataApiClient;
  private readonly inFlightFetches = new Map<string, () => void>();

  constructor() {
    this.dataApiClient = new DataApiClient();
  }

  public refreshGrid = (signalRActions: SignalRActions, resultSetId: string, appInsights: ReactPlugin) => {
    const gridSpecifications = GRID_STORE.getState().gridSpecifications;
    const specification = gridSpecifications[resultSetId];

    const { projectId, queryId, dataNodeId, proposalId } = getResultSetEntry(resultSetId);

    const columnWidthMap = specification.columnWidthsByKey as { [key: string]: number };
    const edgeKeyMap = specification.edgeKeyMap as { [edgeKey: string]: string[] };
    const propertyKeyMap = specification.propertyKeyMap as { [edgeKey: string]: string[] };

    const storeFilters = GRID_STORE.getState().filters[resultSetId];
    if (storeFilters === undefined) {
      return;
    }

    const storePropertyFilters = GRID_STORE.getState().propertyFilters[resultSetId];
    if (storePropertyFilters === undefined) {
      return;
    }

    const storeSortings = GRID_STORE.getState().sortings[resultSetId];
    if (storeSortings === undefined) {
      return;
    }

    const filters = storeFilters as { [columnKey: string]: ColumnFilter };
    const propertyFilters = storePropertyFilters as { [columnKey: string]: PropertyFilter };
    const sorts = storeSortings as number[];

    const columnTraversalIdByColumnKeyMap = specification.nodeTraversalIdByColumnKey as { [columnKey: string]: string };

    const columnSpecificationByIndexMap = specification.columnSpecificationByIndexMap as {
      [number: string]: ColumnSpecification;
    };

    const gridSpecification = specification as GridSpecification;

    const handle = this.fetchQueryResult(
      signalRActions,
      resultSetId,
      projectId,
      queryId,
      columnWidthMap,
      edgeKeyMap,
      propertyKeyMap,
      filters,
      propertyFilters,
      sorts,
      columnTraversalIdByColumnKeyMap,
      columnSpecificationByIndexMap,
      gridSpecification,
      appInsights,
      { type: 'refresh' },
      undefined,
      undefined,
      dataNodeId,
      proposalId
    );

    return handle.resolve();
  };

  public fetchQueryResult = (
    signalRActions: SignalRActions,
    resultSetId: string,
    projectId: string,
    queryId: string,
    columnWidthMap: { [key: string]: number },
    edgeKeyMap: { [edgeKey: string]: string[] },
    propertyKeyMap: { [edgeKey: string]: string[] },
    filters: { [columnKey: string]: ColumnFilter },
    propertyFilters: { [columnKey: string]: PropertyFilter },
    sorts: number[],
    columnTraversalIdByColumnKeyMap: { [columnKey: string]: string },
    columnSpecificationByIndexMap: { [number: string]: ColumnSpecification },
    gridSpecification: GridSpecification,
    appInsights: ReactPlugin,
    gridLoadingContext: GridLoadingContext,
    streamInvocationOverride?: (options: QueryOptions) => DataHubStreamInvocation<IQueryRequestContext>,
    streamSubscription?: (nodes: SemNode[]) => void,
    dataNodeId?: string,
    proposalId?: string
  ): { resolve: () => Promise<void>; cancel: () => void } => {
    this.inFlightFetches.get(resultSetId)?.();

    let subscription: ISubscription<SemNode[]>;
    let resolvePromise: () => void;
    let cancelled = false;

    // eslint-disable-next-line no-async-promise-executor
    const promise = new Promise<void>(async (resolve, reject) => {
      resolvePromise = resolve;
      const connectionResult = await safeResolve(signalRActions.Connect());

      if (cancelled) {
        resolve();
        return;
      }

      if (!connectionResult.success) {
        reject(connectionResult.error);
        return;
      }

      const signalRHubConnection = connectionResult.result;
      const requestFilters = requestFiltersParams(filters, columnTraversalIdByColumnKeyMap);

      const requestPropertyFilters = requestPropertyFiltersParams(
        propertyFilters,
        gridSpecification.columnIndexByKey,
        gridSpecification.columnSpecificationByIndexMap
      );

      const requestsSorts = requestSortParams(sorts, columnSpecificationByIndexMap);

      const streamInvocation =
        streamInvocationOverride !== undefined
          ? streamInvocationOverride({
              DataNodeId: dataNodeId === undefined ? [] : [dataNodeId],
              Filters: requestFilters,
              PropertyFilters: requestPropertyFilters,
              Sorts: requestsSorts
            })
          : ({
              endpoint: 'StreamGridData',
              requestModel: {
                Context: {
                  ProjectId: projectId,
                  QueryId: queryId,
                  DataNodeId: dataNodeId === undefined ? [] : [dataNodeId], // TODO: should be removed
                  ProposalId: proposalId
                } as GridDataRequest,
                Options: {
                  DataNodeId: dataNodeId === undefined ? [] : [dataNodeId],
                  Filters: requestFilters,
                  PropertyFilters: requestPropertyFilters,
                  Sorts: requestsSorts
                }
              }
            } satisfies DataHubStreamInvocation<GridDataRequest>);

      const stream = signalRHubConnection.stream<SemNode[]>(streamInvocation.endpoint, streamInvocation.requestModel);

      GRID_STORE.getState().clear(resultSetId);

      const start = performance.now();
      let rows = 0;
      let numberOfNodes = 0;

      const batches: Promise<void>[] = [];
      subscription = stream.subscribe({
        next: (newNodes: SemNode[]) => {
          rows = rows + newNodes.length;

          const properties = getNodeProperties(newNodes);
          batches.push(
            calculateNodesWithProperties(newNodes, columnWidthMap, edgeKeyMap, propertyKeyMap)
              .then((nodes) => {
                numberOfNodes = numberOfNodes + properties.length;

                streamSubscription?.(newNodes);

                GRID_STORE.getState().addCalculatedNodes(resultSetId, newNodes, nodes, properties);
              })
              .catch((error) => {
                console.error('Error in next function:', error);
              })
          );
        },
        complete: async () => {
          await Promise.all(batches);
          GRID_STORE.getState().complete(resultSetId);

          const stop = performance.now();
          const duration = stop - start;

          const gridProperties = getColumnSpecificationStatistics(columnSpecificationByIndexMap, gridLoadingContext);
          const numberOfFilters = Object.keys(filters).length;

          devLog(`GRID LOADED: ${rows} rows x ${gridProperties.totalNumberOfColumns} columns,  ${duration}ms`);

          appInsights.trackEvent({
            name: 'Grid Loaded',
            properties: {
              rows: rows,
              nodes: numberOfNodes,
              duration: duration,
              isOverview: !hasValue(dataNodeId),
              numberOfSorts: sorts.length,
              numberOfFilters: numberOfFilters,
              gridId: resultSetId,
              projectId: projectId,
              fetchContext: gridLoadingContext.type,
              ...gridProperties
            }
          });

          this.inFlightFetches.delete(resultSetId);
          resolve();
        },
        error: (err) => {
          appInsights.trackException(
            {
              exception: {
                message: `Error while streaming results`,
                name: `ResultStreamError: ${err}`
              }
            },
            {
              queryId: queryId,
              dataNodeId: dataNodeId,
              projectId: projectId,
              filters: requestFilters,
              sorts: requestsSorts
            }
          );

          GRID_STORE.getState().clear(resultSetId);
          GRID_STORE.getState().complete(resultSetId);

          this.inFlightFetches.delete(resultSetId);
          reject(err);
        }
      });
    });

    const cancel = () => {
      cancelled = true;
      subscription?.dispose();
      GRID_STORE.getState().clearIfNotComplete(resultSetId);
      this.inFlightFetches.delete(resultSetId);
      resolvePromise?.();
    };

    this.inFlightFetches.set(resultSetId, cancel);

    return {
      resolve: async () => await promise,
      cancel
    };
  };

  public getEditRelationGridSpecification = (
    projectId: string,
    queryId: string,
    queryNodeId: string,
    queryRelationId: string,
    parentDataNodeId: string,
    proposalId?: string
  ) => {
    return this.dataApiClient
      .GetEditRelationsGrid(projectId, queryId, queryNodeId, queryRelationId, parentDataNodeId, proposalId)
      .then((result) => {
        if (result.success) {
          GRID_STORE.getState().initializePanelDataSets([result.result]);

          return result.result.resultSetId;
        } else {
          throw result.error;
        }
      })
      .catch((reason) => {
        throw reason;
      });
  };

  public saveSemanticChanges = async (
    projectId: string,
    queryId: string,
    queryNodeId: string,
    rootDataNodeId: string,
    parentDataNodeId: string,
    itemsRemoved: string[],
    itemsAdded: string[],
    itemsCreated: string[],
    viewingDataNodeId?: string,
    proposalId?: string
  ) => {
    const resultSetId = createResultSetEntryId(projectId, proposalId, queryId, viewingDataNodeId);

    trackEvent(`Relationships Changed`, 'Project', {
      projectId: projectId,
      itemsAdded: itemsAdded.length,
      itemsCreated: itemsCreated.length,
      itemsRemoved: itemsRemoved.length,
      isDetailPanel: viewingDataNodeId !== undefined
    });

    const result = await this.dataApiClient.SaveRelations(
      resultSetId,
      projectId,
      queryId,
      queryNodeId,
      rootDataNodeId,
      parentDataNodeId,
      itemsRemoved,
      itemsAdded,
      itemsCreated
    );

    PENDING_EDITS_STATE.getState().addPendingRelationshipChange(
      projectId,
      parentDataNodeId,
      result.EventId,
      result.UserEmail
    );
  };

  public requestSemanticUpdates = (
    signalRActions: SignalRActions,
    updatedParentNodeIds: string[],
    instructionToIgnore: RequestResultSetUpdateInstruction[],
    reason?: {
      pendingEventId: string;
      originatingResultSetId: string;
      queryNodeId: string;
      connectedNodeIds: string[];
      parentNodeId?: string;
    }
  ) => {
    const { rawNodes, completedResultSets, filters, propertyFilters, sortings, gridSpecifications } =
      GRID_STORE.getState();

    const directUpdates = updatedParentNodeIds.flatMap((n) => {
      return getResultSetsToUpdate(
        rawNodes,
        completedResultSets,
        filters,
        propertyFilters,
        sortings,
        n,
        instructionToIgnore
      );
    });

    if (
      reason !== undefined &&
      reason.originatingResultSetId.length > 0 &&
      reason.originatingResultSetId.includes('_')
    ) {
      const possibleNewRootNodes = GetPossibleNewRoots(
        reason.originatingResultSetId,
        reason.queryNodeId,
        reason.connectedNodeIds,
        rawNodes,
        completedResultSets,
        gridSpecifications,
        filters,
        propertyFilters,
        sortings,
        reason.parentNodeId
      );
      directUpdates.push(...possibleNewRootNodes);
    }

    if (directUpdates.length > 0) {
      updateRootRows(signalRActions, directUpdates, reason?.pendingEventId);
    }
  };

  public requestSemanticUpdate = (
    signalRActions: SignalRActions,
    updatedParentNodeId: string,
    instructionToIgnore: RequestResultSetUpdateInstruction[]
  ) => {
    this.requestSemanticUpdates(signalRActions, [updatedParentNodeId], instructionToIgnore);
  };

  public DeleteNode = async (
    projectId: string,
    templateNodeId: string,
    dataNodeId: string,
    queryNodeId: string,
    resultSetId: string,
    clearedNodes: { [key: string]: string[] },
    clearedRelations: { [key: string]: string[] },
    parentDataRelationId?: string
  ) => {
    trackEvent('Delete Node', 'Project', { projectId });

    const result = await this.dataApiClient.DeleteDataNode(
      projectId,
      templateNodeId,
      dataNodeId,
      queryNodeId,
      resultSetId,
      clearedNodes,
      clearedRelations,
      parentDataRelationId
    );

    DELETE_NODE_STATE.getState().waitForEventCompletion(result);

    return result;
  };

  getNodePropertyProjectHistory = (projectId: string, nodeId: string, propertyId: string) => {
    trackEvent('Project History Node Property ', 'Project', { projectId });

    HISTORY_STORE.getState().setIsFetching(true);
    HISTORY_STORE.getState().open(projectId);

    return this.dataApiClient
      .GetNodePropertyProjectHistory(projectId, nodeId, propertyId)
      .then((result) => {
        if (result !== undefined) {
          const historyProcessFilterDuplicates = addOldValuePropToEventsHistory(result);

          HISTORY_STORE.getState().setEventsHistory(projectId, nodeId, propertyId, historyProcessFilterDuplicates);
        }
      })
      .finally(() => {
        HISTORY_STORE.getState().setIsFetching(false);
      });
  };
}

export const addOldValuePropToEventsHistory = (result: OutputOperationResult<EventHistoryInfo[]>) => {
  const rawEventsHistoryInfo = result as unknown as EventHistoryInfo[];
  const rawEventsHistoryInfoSortByTime = rawEventsHistoryInfo
    ?.slice()
    .sort((a, b) => new Date(b.timeStamp).getTime() - new Date(a.timeStamp).getTime());

  const historyProcess = rawEventsHistoryInfoSortByTime.map((eventHistoryInfo: EventHistoryInfo, index) => {
    return {
      ...eventHistoryInfo,
      oldValue: rawEventsHistoryInfoSortByTime[index + 1]?.value ?? ''
    } as unknown as EventHistoryInfo;
  });

  // Ensure created events are not filtered out
  return historyProcess.filter(({ oldValue, value, operation }) => {
    return operation === EventLogOperation.created || oldValue !== value;
  });
};
